import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Modal as GarnishModal, useResponsive } from '@sg/garnish';
import { type PaymentMethodCard } from '@sg/graphql-schema';

import { useTrackEventEffect } from '@order/Telemetry';

import { useSubmitPaymentMethod } from '../hooks';
import { CreditCardFormAdd } from './CreditCardFormAdd';

// ─────────────────────────────────────────────────────────────────────────────

export const CreditCardFormAddModal = (props: CreditCardFormAddModalProps) => {
  const { visible, onRequestClose, Modal: CustomModal } = props;
  const onSuccess = props.onSuccess ?? onRequestClose;

  useTrackEventEffect({ name: 'payment_credit.add_card', skip: !visible });

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  const { isSubmitting, submitPaymentMethod } = useSubmitPaymentMethod({
    onSuccess,
  });

  // Use the custom Modal if provided, otherwise use the default Modal from garnish
  const Modal = CustomModal ?? GarnishModal;

  return (
    <Modal
      fitHeight
      visible={visible}
      accessibilityLabel={formatMessage(messages.creditCardFormAdd)}
      presentationStyle="pageSheet"
      size={match(['full', 'small'])}
      onRequestClose={onRequestClose}
    >
      <Modal.Header onClose={onRequestClose}>
        {formatMessage(messages.creditCardFormAdd)}
      </Modal.Header>

      <SafeAreaView edges={['bottom']} style={styles.container}>
        <CreditCardFormAdd
          isSubmitting={isSubmitting}
          handleOnSave={submitPaymentMethod}
        />
      </SafeAreaView>
    </Modal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  creditCardFormAdd: {
    defaultMessage: 'Add card',
    description: 'Credit Card Form | Add',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CreditCardFormAddModalProps = Readonly<{
  visible: boolean;
  onRequestClose: () => void;

  /**
   * Optional callback to differentiate closing the modal vs submitting the form.
   * If not provided, {onRequestClose} will be used instead.
   */
  onSuccess?: (paymentMethod: PaymentMethodCard) => void;

  /**
   * Optional Modal component to use instead of the default Modal from garnish.
   */
  Modal?: typeof GarnishModal;
}>;
